/* Clothxtra product slider: related products + recently viewed.
   Lives inside <cx-collection>, which provides the + quick view, add to cart and money formatting. */
if (!customElements.get('cx-rail')) {
  const RECENT_KEY = 'cx_recently_viewed';

  class CxRail extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('cx-collection');
      if (this.dataset.mode === 'related') this.loadRelated();
      else if (this.dataset.mode === 'recent') this.loadRecent();
      else this.initArrows();
    }

    hideSection() {
      const section = this.closest('.shopify-section');
      if (section) section.style.display = 'none';
      else if (this.host) this.host.hidden = true;
    }

    /* ---------- Related / complementary (Shopify recommendations) ---------- */
    async loadRelated() {
      if (this.querySelector('[data-cx-rail-track]')) {
        this.initArrows();
        return;
      }
      const load = async (url) => {
        const res = await fetch(url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const el = doc.querySelector('cx-rail[data-mode="related"]');
        return el && el.querySelector('[data-cx-rail-track] > li') ? el : null;
      };
      try {
        let fresh = await load(this.dataset.url);
        // "Frequently bought together" (complementary) is empty until it's set up in
        // Search & Discovery — fall back to related products so the slider still shows.
        if (!fresh && this.dataset.url.includes('intent=complementary')) {
          fresh = await load(this.dataset.url.replace('intent=complementary', 'intent=related'));
        }
        if (!fresh) {
          this.hideSection();
          return;
        }
        this.innerHTML = fresh.innerHTML;
        this.initArrows();
      } catch (e) {
        console.error('cx-related', e);
        this.hideSection();
      }
    }

    /* ---------- Recently viewed (stored in this browser) ---------- */
    readRecent() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      } catch (e) {
        return [];
      }
    }

    async loadRecent() {
      const current = this.dataset.current;
      let list = this.readRecent().filter((h) => typeof h === 'string' && h);

      // remember the product being viewed now (newest first)
      if (current) {
        const next = [current, ...list.filter((h) => h !== current)].slice(0, 20);
        try {
          localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        } catch (e) {
          /* storage unavailable (private mode) */
        }
      }

      const limit = Number(this.dataset.limit) || 10;
      list = list.filter((h) => h !== current).slice(0, limit);
      if (!list.length) {
        this.hideSection();
        return;
      }

      const root = window.Shopify?.routes?.root || '/';
      const results = await Promise.all(
        list.map((handle) =>
          fetch(`${root}products/${encodeURIComponent(handle)}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null)
        )
      );
      const products = results.filter(Boolean);

      // forget products that no longer exist
      if (products.length !== list.length) {
        const ok = new Set(products.map((p) => p.handle));
        try {
          const stored = this.readRecent().filter((h) => h === current || ok.has(h) || !list.includes(h));
          localStorage.setItem(RECENT_KEY, JSON.stringify(stored));
        } catch (e) {
          /* ignore */
        }
      }

      if (!products.length) {
        this.hideSection();
        return;
      }

      const track = this.querySelector('[data-cx-rail-track]');
      track.innerHTML = products.map((p) => this.cardHTML(p)).join('');
      if (this.host) this.host.hidden = false;
      this.initArrows();
    }

    cardHTML(p) {
      const h = this.host;
      const esc = (v) => (h ? h.escape(v) : String(v ?? ''));
      const img = (src, w) => (h ? h.imgUrl(src, w) : src);
      const money = (c) => (h ? h.money(c) : String(c / 100));
      const d = this.dataset;
      const url = p.url || `/products/${p.handle}`;
      const hideTitle = d.hideTitle === 'true';
      const first = p.featured_image || (p.images && p.images[0]);
      const second = d.secondImage === 'true' && p.images && p.images[1];
      const single = p.variants && p.variants.length === 1;
      const compare = p.compare_at_price && p.compare_at_price > p.price ? p.compare_at_price : 0;

      let add = '';
      if (d.quickAdd === 'true' && p.available) {
        add = single
          ? `<button type="button" class="cx-pc__add" data-cx-add="${p.variants[0].id}" aria-label="${esc(d.add)}: ${esc(p.title)}"><span class="cx-pc__plus" aria-hidden="true"></span></button>`
          : `<button type="button" class="cx-pc__add" data-cx-qv-open="${esc(p.handle)}" data-url="${esc(url)}" aria-haspopup="dialog" aria-label="${esc(d.choose)}: ${esc(p.title)}"><span class="cx-pc__plus" aria-hidden="true"></span></button>`;
      }

      return `<li class="cx-pc">
        <div class="cx-pc__media">
          <a href="${esc(url)}" class="cx-pc__img-link" ${hideTitle ? `aria-label="${esc(p.title)}"` : 'tabindex="-1" aria-hidden="true"'}>
            ${first ? `<img class="cx-pc__img" src="${img(first, 600)}" srcset="${img(first, 360)} 360w, ${img(first, 600)} 600w, ${img(first, 900)} 900w" sizes="(min-width: 990px) 20vw, 45vw" loading="lazy" alt="${esc(p.title)}">` : ''}
            ${second ? `<img class="cx-pc__img cx-pc__img--2" src="${img(second, 600)}" loading="lazy" alt="">` : ''}
          </a>
          ${add}
          ${this.flagHTML(p, esc)}
          ${p.available ? '' : `<span class="cx-pc__soldout">${esc(d.soldOut)}</span>`}
        </div>
        <div class="cx-pc__info">
          ${hideTitle ? '' : `<h3 class="cx-pc__title"><a href="${esc(url)}">${esc(p.title)}</a></h3>`}
          <p class="cx-pc__price">
            <span class="cx-pc__now">${money(p.price)}</span>
            ${compare ? `<s class="cx-pc__was">${money(compare)}${esc(h?.dataset.asterisk || '')}</s>` : ''}
          </p>
        </div>
      </li>`;
    }

    /* Image flag from a product tag like "flag:Bestseller" (same as snippets/cx-pc-flag.liquid) */
    flagHTML(p, esc) {
      const tag = (p.tags || []).find((t) => String(t).toLowerCase().trim().includes('flag:'));
      if (!tag) return '';
      const label = String(tag).split(':').pop().trim();
      if (!label) return '';
      const f = label.toLowerCase();
      const icons = {
        star: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="8.6" fill="none" stroke="currentColor" stroke-width="1.6"/><path fill="currentColor" d="m10 5.4 1.35 2.75 3.03.44-2.19 2.13.52 3.02L10 12.32l-2.71 1.42.52-3.02-2.19-2.13 3.03-.44z"/></svg>',
        trend: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="m2.5 14.5 5-5 3.2 3.2 6.8-6.8"/><path d="M12.5 5.9h5v5"/></svg>',
        new: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" d="M10 2.5c.5 3.6 1.9 5 5.5 5.5-3.6.5-5 1.9-5.5 5.5-.5-3.6-1.9-5-5.5-5.5 3.6-.5 5-1.9 5.5-5.5zM15.5 12.5c.25 1.6.9 2.25 2.5 2.5-1.6.25-2.25.9-2.5 2.5-.25-1.6-.9-2.25-2.5-2.5 1.6-.25 2.25-.9 2.5-2.5z"/></svg>',
        hot: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M10 18c-3.3 0-5.5-2.3-5.5-5.3 0-3.6 3.2-5 3.5-9.2 2.6 1.5 3.6 3.6 3.4 5.6 1-.5 1.6-1.4 1.8-2.6 1.7 1.5 2.3 3.4 2.3 5.1C15.5 15.7 13.3 18 10 18z"/></svg>',
        clock: '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="10" cy="10" r="8"/><path d="M10 5.5V10l3 1.8"/></svg>',
      };
      let icon = '';
      if (f.includes('best')) icon = icons.star;
      else if (f.includes('trend')) icon = icons.trend;
      else if (f.includes('new')) icon = icons.new;
      else if (f.includes('hot') || f.includes('fast') || f.includes('popular')) icon = icons.hot;
      else if (f.includes('limited') || f.includes('last')) icon = icons.clock;
      return `<span class="cx-flag">${icon}<span>${esc(label)}</span></span>`;
    }

    /* ---------- Arrows (desktop) ---------- */
    initArrows() {
      const track = this.querySelector('[data-cx-rail-track]');
      const prev = this.querySelector('[data-cx-rail-prev]');
      const next = this.querySelector('[data-cx-rail-next]');
      if (!track || !prev || !next) return;

      const step = () => {
        const card = track.querySelector(':scope > li');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const cardW = card ? card.getBoundingClientRect().width + gap : track.clientWidth;
        // move by whole cards, about one screen at a time
        return Math.max(cardW, Math.floor(track.clientWidth / cardW) * cardW);
      };
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth;
        prev.hidden = track.scrollLeft <= 4;
        next.hidden = track.scrollLeft >= max - 4 || max <= 4;
      };

      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      track.querySelectorAll('img').forEach((i) => !i.complete && i.addEventListener('load', sync, { once: true }));
      sync();
    }
  }

  customElements.define('cx-rail', CxRail);
}